import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RefreshCw, Loader2, Sparkles, Info, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// Fallback bullets if the /api/app-release call fails or has no notes saved.
const WHATS_NEW = [
  "Pro-grade food search — 3× more foods, plus the USDA database & better branded results",
  "Add TrainingPeaks Premium (£9.99/mo) right at checkout to auto-sync your sessions",
  "Tracker tidied — every section is now a clean tap-to-open dropdown with branded headers",
  "Tracker is now nutrition-only — sleep, hydration and session feedback have been removed",
  "Sharper, more readable text in light mode",
];

// -----------------------------------------------------------------------------
// This component handles TWO different "there's a new version" scenarios:
//
// 1) FORCED UPDATE (blocking) — the JS bundle running in this tab is stale
//    (a deploy landed while the tab was open, or the device has a stale cache).
//    Detected by watching the fingerprinted main bundle in index.html and
//    comparing against what's actually loaded. Users MUST reload.
//
// 2) WHAT'S NEW (dismissable) — the RELEASE VERSION (set in Admin) has changed
//    since this device last acknowledged it. Fires on any visit after a deploy,
//    NOT just when a tab was already open. Tracked in localStorage. First-ever
//    load silently seeds the version so brand-new users don't get spammed.
// -----------------------------------------------------------------------------
const POLL_MS = 30_000;
const MAIN_RE = /\/static\/js\/main\.[A-Za-z0-9]+\.js/;
const SEEN_KEY = "spc_release_seen";

function loadedMain() {
  try {
    for (const s of document.querySelectorAll("script[src]")) {
      const m = (s.getAttribute("src") || "").match(MAIN_RE);
      if (m) return m[0];
    }
  } catch { /* ignore */ }
  return null;
}

async function serverMain() {
  try {
    const res = await fetch(`/index.html?_=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) return null;
    const m = (await res.text()).match(MAIN_RE);
    return m ? m[0] : null;
  } catch {
    return null;
  }
}

// Stable-ish id used to decide "has the user seen these notes yet?". Prefers
// the admin-set version label; falls back to a djb2 hash of the notes so
// simply editing bullets triggers the popup even if the label was left empty.
function releaseId({ version, notes }) {
  if (version && version.trim()) return `v:${version.trim()}`;
  const s = (notes || []).join("|");
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h) + s.charCodeAt(i); // eslint-disable-line no-bitwise
  return `h:${(h >>> 0).toString(36)}`; // eslint-disable-line no-bitwise
}

export default function AppUpdater() {
  const { email } = useAuth();
  const isAuthed = Boolean(email);
  const [baseline] = useState(loadedMain);
  const [hasUpdate, setHasUpdate] = useState(false);   // #1 forced update
  const [showWhatsNew, setShowWhatsNew] = useState(false); // #2 dismissable
  const [updating, setUpdating] = useState(false);
  const [release, setRelease] = useState({ notes: WHATS_NEW, version: "" });

  // Only fetch (and possibly show) the "What's New" popup once the user is
  // signed in — otherwise the login/marketing page gets an unnecessary popup
  // on every visit.
  useEffect(() => {
    if (!isAuthed) return;
    fetch("/api/app-release", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        const rel = {
          notes: (Array.isArray(d.notes) && d.notes.length) ? d.notes : WHATS_NEW,
          version: d.version || "",
        };
        setRelease(rel);
        // Decide whether to show the "What's New" popup for this device.
        // Any device whose stored "seen" id doesn't match the current release
        // sees the popup once — including devices with no stored id yet, so
        // existing production users get the popup on their next visit after
        // a deploy (which is the whole point). Brand-new signups will see it
        // once too, which is fine — it doubles as a friendly "here's what
        // just shipped" welcome.
        try {
          const id = releaseId(rel);
          const seen = localStorage.getItem(SEEN_KEY);
          if (seen !== id) setShowWhatsNew(true);
        } catch { /* localStorage might be blocked */ }
      })
      .catch(() => {});
  }, [isAuthed]);

  const check = useCallback(async () => {
    if (!baseline || hasUpdate) return;
    const s = await serverMain();
    if (s && s !== baseline) setHasUpdate(true);
  }, [baseline, hasUpdate]);

  useEffect(() => {
    if (!baseline) return;
    const first = setTimeout(check, 800);
    const id = setInterval(check, POLL_MS);
    const onVis = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", check);
    return () => {
      clearTimeout(first);
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", check);
    };
  }, [baseline, check]);

  // Lock scroll ONLY while the blocking forced-update overlay is up. The
  // dismissable "What's New" popup lets the user keep scrolling behind it.
  useEffect(() => {
    if (!hasUpdate) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [hasUpdate]);

  const update = async () => {
    setUpdating(true);
    try {
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      if (window.caches) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch { /* noop */ }
    window.location.reload();
  };

  const dismissWhatsNew = () => {
    try { localStorage.setItem(SEEN_KEY, releaseId(release)); } catch { /* noop */ }
    setShowWhatsNew(false);
  };

  return (
    <AnimatePresence>
      {hasUpdate && (
        <motion.div key="app-update" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/75 backdrop-blur-sm p-4"
          data-testid="app-update-overlay">
          <motion.div initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.25 }}
            className="force-dark w-full max-w-sm border border-white/15 bg-[#141414] p-6 text-white text-center rounded-none">
            <div className="inline-flex items-center justify-center h-12 w-12 border border-[#E2E30A]/40 bg-[#E2E30A]/10 mb-4">
              <Sparkles className="h-5 w-5 text-[#E2E30A]" />
            </div>
            <p className="font-display text-2xl uppercase tracking-tight text-white">Update required</p>
            <p className="text-sm text-zinc-100 mt-2">A newer version of Performance Nutrition is available. You must update before continuing so you're always on the latest version.</p>
            <div className="mt-4 text-left border border-white/20 bg-black/60 p-4 rounded-md" data-testid="whats-new">
              <p className="text-xs uppercase tracking-[0.2em] text-[#E2E30A] font-bold flex items-center gap-1.5 mb-2.5"><Info className="h-4 w-4" /> What's new{release.version ? ` · ${release.version}` : ""}</p>
              <ul className="space-y-2 max-h-[45vh] overflow-y-auto pr-1 spc-scroll">
                {release.notes.map((t, i) => (
                  <li key={i} className="text-sm text-white leading-snug flex gap-2"><span className="text-[#E2E30A] shrink-0">•</span><span>{t}</span></li>
                ))}
              </ul>
            </div>
            <button type="button" onClick={update} disabled={updating} data-testid="app-update-btn"
              className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black font-bold uppercase tracking-wider py-3 transition-colors disabled:opacity-60">
              {updating ? <><Loader2 className="h-4 w-4 animate-spin" /> Updating…</> : <><RefreshCw className="h-4 w-4" /> Update now</>}
            </button>
          </motion.div>
        </motion.div>
      )}

      {/* Dismissable "What's New" popup — fires once per release for every user. */}
      {!hasUpdate && showWhatsNew && isAuthed && (
        <motion.div key="whats-new-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[95] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          data-testid="whats-new-overlay" onClick={dismissWhatsNew}>
          <motion.div initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()}
            className="force-dark relative w-full max-w-md border border-[#E2E30A]/40 bg-[#141414] p-6 text-white rounded-none">
            <button type="button" onClick={dismissWhatsNew} data-testid="whats-new-close"
              aria-label="Close" className="absolute top-3 right-3 p-1 text-zinc-400 hover:text-white transition-colors">
              <X className="h-4 w-4" />
            </button>
            <div className="inline-flex items-center justify-center h-11 w-11 border border-[#E2E30A]/40 bg-[#E2E30A]/10 mb-3">
              <Sparkles className="h-5 w-5 text-[#E2E30A]" />
            </div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#E2E30A] font-bold">Just shipped</p>
            <p className="font-display text-2xl uppercase tracking-tight text-white mt-1">What's new{release.version ? ` · ${release.version}` : ""}</p>
            <p className="text-sm text-zinc-300 mt-2">Fresh updates landed since you last opened Performance Nutrition.</p>
            <div className="mt-4 border border-white/10 bg-black/60 p-4 rounded-md" data-testid="whats-new-list">
              <ul className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
                {release.notes.map((t, i) => (
                  <li key={i} className="text-sm text-white leading-snug flex gap-2"><span className="text-[#E2E30A] shrink-0">•</span><span>{t}</span></li>
                ))}
              </ul>
            </div>
            <button type="button" onClick={dismissWhatsNew} data-testid="whats-new-ok-btn"
              className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black font-bold uppercase tracking-wider py-3 transition-colors">
              Got it
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
